import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import CoachVoiceButton from "../AICoach/CoachVoiceButton";
import DigipingMark from "../../digiping/DigipingMark";
import { sendMessage } from "../funtionsBD/aiCoachService";
import { currentAppLanguage } from "../../utils/appLanguages";
import { detectInputLanguage } from "../../utils/detectInputLanguage";
import { toCoachLanguage } from "../../voice";
import {
  parsePlanningVoiceCommand,
  planningExerciseVoiceHints,
} from "../../utils/planningVoiceCommands";
import { readPlanningFocus } from "../../utils/planningSubgroups";
import { localizedExerciseName } from "../../utils/localizedCatalogFields";
import "../../pages/LiveTraining/LiveTrainingTableSetupPingfloVoice.css";
import "./PlanningPingfloVoice.css";

/** Coach reviews the transcript and taps Send (same as table setup). */
export const PLANNING_VOICE_REQUIRE_CONFIRM = true;

function LevelMeter({ level = 0, active = false }) {
  const bars = 7;
  const normalized = Math.max(0, Math.min(1, Number(level) || 0));
  return (
    <div
      className={`lt-pingflo-voice__meter${active ? " is-active" : ""}`}
      aria-hidden
    >
      {Array.from({ length: bars }, (_, i) => {
        const threshold = (i + 1) / (bars + 1);
        const lit = active && normalized >= threshold * 0.55;
        const height = 28 + ((i % 3) + 1) * 10;
        return (
          <span
            key={i}
            className={`lt-pingflo-voice__bar${lit ? " is-lit" : ""}`}
            style={{
              height: `${height}%`,
              opacity: lit ? 0.55 + normalized * 0.45 : 0.22,
              transform: lit
                ? `scaleY(${0.55 + normalized * 0.7})`
                : "scaleY(0.35)",
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Pingflo voice for the training-session planning board.
 * Local phonetic parse first, then AI coach with planning context.
 */
export function PlanningPingfloVoice({
  disabled = false,
  tsId = null,
  grId = null,
  primaryColor = "#0877BC",
  exercises = [],
  selectedExercise = null,
  selectedTsgId = null,
  stationName = null,
  onVoiceCommand = null,
  className = "",
  /** CSS selector for the caption portal host (defaults to planning board slot). */
  captionSlotSelector = ".psg-board__pingflo-slot",
  /** Element that receives the open-state class while the caption is visible. */
  openClassSelector = ".psg-board",
  openClassName = "psg-board--pingflo-open",
  /** Context page sent to the AI coach (`planning` | `live_training`). */
  contextPage = "planning",
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [livePartial, setLivePartial] = useState("");
  const [voiceLanguage, setVoiceLanguage] = useState(null);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null);
  const [error, setError] = useState("");
  const [listening, setListening] = useState(false);
  const [level, setLevel] = useState(0);
  const [editing, setEditing] = useState(false);
  /** True only after final STT (or coach edit) — keeps Send off during live partials. */
  const [transcriptReady, setTranscriptReady] = useState(false);
  const [autoStartToken, setAutoStartToken] = useState(0);
  const conversationIdRef = useRef(null);
  const textareaRef = useRef(null);
  const editingRef = useRef(false);
  const userEditedRef = useRef(false);
  /** idle | live | final — final blocks a late listening(true) from wiping the transcript. */
  const voicePhaseRef = useRef("idle");
  const freshCommandRef = useRef(true);

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  useEffect(() => {
    if (!open) return undefined;
    const board =
      (typeof document !== "undefined" &&
        document.querySelector(openClassSelector)) ||
      null;
    if (!board) return undefined;
    board.classList.add(openClassName);
    return () => board.classList.remove(openClassName);
  }, [open, openClassName, openClassSelector]);

  const clearCaptionText = useCallback(() => {
    setDraft("");
    setLivePartial("");
    setEditing(false);
    setTranscriptReady(false);
  }, []);

  const openMic = useCallback(() => {
    if (disabled || sending || !tsId) return;
    setError("");
    setReply(null);
    setOpen(true);
    setAutoStartToken((n) => n + 1);
  }, [disabled, sending, tsId]);

  const executeLocalCommand = useCallback(
    async (cmd) => {
      if (!cmd || typeof onVoiceCommand !== "function") return null;
      if (cmd.incomplete) {
        if (cmd.reason === "need_exercise") {
          return {
            ok: false,
            message:
              t("planningVoiceNeedExercise") ||
              "Select a training block, or name the exercise.",
          };
        }
        return {
          ok: false,
          message: t("error") || "Could not understand.",
        };
      }

      const ok = await onVoiceCommand({
        type: cmd.action,
        exercise: cmd.exercise || null,
        description: cmd.description || "",
        query: cmd.query || "",
      });
      if (ok === null) return null;

      const messages = {
        add_exercise: t("planningVoiceAddedExercise") || "Opening add exercise…",
        edit_exercise: t("planningVoiceEditing") || "Opening edit…",
        add_sequence: t("planningVoiceAddSequence") || "Adding a sequence…",
        apply_template: t("planningVoiceTemplate") || "Opening templates…",
        create_personalized:
          t("planningVoicePersonalized") || "Opening personalized slot…",
        create_custom: t("planningVoiceCustom") || "Opening custom exercise…",
        select_catalog: t("planningVoiceCatalog") || "Opening catalog…",
        create_station:
          t("planningVoiceCreateStation") || "Opening new station…",
        ai_plan: t("planningVoiceAiPlan") || "Opening AI session plan…",
      };
      return {
        ok: Boolean(ok),
        message:
          messages[cmd.action] ||
          t("liveTrainingTableWizardVoiceApplied") ||
          "Done.",
      };
    },
    [onVoiceCommand, t]
  );

  const sendCommand = useCallback(
    async (rawText, languageHint = null) => {
      const text = (rawText || "").trim();
      if (!text || sending || disabled) return;

      setSending(true);
      setError("");
      setReply(null);

      try {
        const lng = languageHint || currentAppLanguage(i18n);
        const local = parsePlanningVoiceCommand(text, {
          exercises,
          selectedExercise,
          selectedTeId: selectedExercise?.te_id,
          language: lng,
        });
        if (local && !local.deferToAi && onVoiceCommand) {
          const result = await executeLocalCommand(local);
          if (result) {
            clearCaptionText();
            freshCommandRef.current = true;
            if (result.ok) {
              setReply(result.message);
              setOpen(false);
            } else {
              setError(result.message);
            }
            return;
          }
        }

        const focus = readPlanningFocus() || {};
        const detectedLang =
          detectInputLanguage(text) ||
          (languageHint ? toCoachLanguage(languageHint) : null);
        const context = {
          ts_id: tsId || focus.ts_id || null,
          gr_id: grId || focus.gr_id || null,
          te_id: selectedExercise?.te_id || focus.te_id || null,
          tsg_id: selectedTsgId || focus.tsg_id || null,
          station_name: stationName || focus.station_name || null,
          exercise_name:
            localizedExerciseName(
              selectedExercise,
              currentAppLanguage(i18n)
            ) ||
            selectedExercise?.ex_name ||
            focus.exercise_name ||
            null,
          page: contextPage || "planning",
          session_plan: true,
          live_training: contextPage === "live_training",
          home_role: "coach",
          concise_replies: true,
          planning_exercise_names: planningExerciseVoiceHints(
            exercises,
            currentAppLanguage(i18n)
          ),
          language: detectedLang || currentAppLanguage(i18n),
          player_search_scope: "club",
          restrict_players_to_club: true,
          restrict_players_to_group: true,
        };
        try {
          const stored = JSON.parse(localStorage.getItem("user") || "null");
          context.club_id =
            stored?.club_id || stored?.club_details?.club_id || null;
          context.club_name =
            stored?.club_details?.club_name || stored?.club_name || null;
        } catch {
          /* ignore */
        }
        if (local?.description) {
          context.voice_description = local.description;
        }
        if (local?.action) {
          context.voice_intent = local.action;
        }

        const result = await sendMessage(
          text,
          conversationIdRef.current,
          context,
          { input_mode: "voice" }
        );
        if (result?.conversationId) {
          conversationIdRef.current = result.conversationId;
        }
        if (result?.planningMutated || context.page === "planning" || context.page === "live_training") {
          window.dispatchEvent(new CustomEvent("smarttt:planning-refresh"));
          if (context.page === "live_training") {
            window.dispatchEvent(new CustomEvent("smarttt:live-plan-refresh"));
          }
        }
        if (result?.actionResult && onVoiceCommand) {
          await onVoiceCommand({
            type: "ai_action",
            actionResult: result.actionResult,
            description: local?.description || text,
          });
        }
        const botText = (result?.message || "").trim();
        clearCaptionText();
        freshCommandRef.current = true;
        setReply(
          botText ||
            t("liveTrainingTableWizardVoiceApplied") ||
            "Done."
        );
        setOpen(false);
      } catch (err) {
        setError(
          err?.message ||
            t("liveTrainingTableWizardVoiceError") ||
            "Voice command failed."
        );
      } finally {
        setSending(false);
      }
    },
    [
      clearCaptionText,
      contextPage,
      disabled,
      executeLocalCommand,
      exercises,
      grId,
      i18n,
      onVoiceCommand,
      selectedExercise,
      selectedTsgId,
      sending,
      stationName,
      t,
      tsId,
    ]
  );

  const handlePartial = useCallback((text) => {
    const cleaned = (text || "").trim();
    if (freshCommandRef.current && cleaned) {
      freshCommandRef.current = false;
      setDraft(cleaned);
      setLivePartial(cleaned);
      setReply(null);
      setError("");
      setEditing(false);
      return;
    }
    setLivePartial(cleaned);
    if (!editingRef.current && cleaned) setDraft(cleaned);
  }, []);

  const handleTranscript = useCallback(
    ({ text, language }) => {
      const cleaned = (text || "").trim();
      if (!cleaned) return;
      if (freshCommandRef.current) freshCommandRef.current = false;
      setError("");
      setReply(null);
      setVoiceLanguage(language || null);
      setLivePartial("");
      if (voicePhaseRef.current === "live") voicePhaseRef.current = "final";
      if (PLANNING_VOICE_REQUIRE_CONFIRM) {
        if (!userEditedRef.current) setDraft(cleaned);
        setTranscriptReady(true);
        setOpen(true);
        return;
      }
      setTranscriptReady(true);
      void sendCommand(cleaned, language);
    },
    [sendCommand]
  );

  const handleListeningChange = useCallback((isListening) => {
    const on = Boolean(isListening);
    if (on) {
      if (voicePhaseRef.current === "live" || voicePhaseRef.current === "final") return;
      voicePhaseRef.current = "live";
      setListening(true);
      setTranscriptReady(false);
      // Fresh STT buffer for this take — prior validated draft is replaced.
      freshCommandRef.current = true;
      userEditedRef.current = false;
      setLivePartial("");
      if (!editingRef.current) {
        setDraft("");
        setReply(null);
        setError("");
      }
      return;
    }
    // Stay not-ready until onFinal validates the transcript (PROCESSING gap).
    voicePhaseRef.current = "idle";
    setListening(false);
    setLevel(0);
  }, []);

  const languageHint = currentAppLanguage(i18n) || "en";
  const displayLive = listening && !editing ? livePartial || draft : draft;
  const canSend =
    transcriptReady &&
    Boolean(String(draft || "").trim()) &&
    !listening &&
    !sending &&
    !disabled;
  const micDisabled = disabled || sending || !tsId;
  const statusLabel = listening
    ? t("liveTrainingTableVoiceListening") || "Listening…"
    : editing
      ? t("liveTrainingTableVoiceEditing") || "Editing command"
      : transcriptReady && draft.trim()
        ? t("liveTrainingTableVoiceReady") || "Ready to send"
        : t("liveTrainingTableVoiceIdle") || "Tap mic and speak";

  const captionHost =
    typeof document !== "undefined"
      ? document.querySelector(captionSlotSelector) ||
        document.querySelector(".psg-board__pingflo-slot") ||
        document.querySelector(".psg-board") ||
        document.body
      : null;

  return (
    <div className={`planning-pingflo ${className}`.trim()}>
      {PLANNING_VOICE_REQUIRE_CONFIRM && open
        ? createPortal(
            <div
              className="lt-pingflo-voice__portal lt-pingflo-voice__portal--caption planning-pingflo__portal"
              role="presentation"
            >
              <div
                className={`lt-pingflo-voice__caption${
                  listening ? " is-listening" : ""
                }${editing ? " is-editing" : ""}`}
                role="region"
                aria-label={
                  t("liveTrainingTableWizardVoiceLabel") || "Pingflo"
                }
                style={{ "--lt-brand": primaryColor }}
              >
                <span className="lt-pingflo-voice__mark" aria-hidden>
                  <DigipingMark
                    agentId="pingflo"
                    size={22}
                    primary={primaryColor}
                    showGlow={listening}
                    title={
                      t("liveTrainingTableWizardVoiceLabel") || "Pingflo"
                    }
                  />
                </span>
                <div className="lt-pingflo-voice__field-wrap">
                  <p
                    className={`lt-pingflo-voice__status${
                      listening ? " is-live" : ""
                    }`}
                    aria-live="polite"
                  >
                    {listening ? <GraphicEqIcon fontSize="inherit" /> : null}
                    {statusLabel}
                  </p>
                  <textarea
                    ref={textareaRef}
                    className="lt-pingflo-voice__input"
                    rows={editing ? 2 : 1}
                    value={displayLive}
                    disabled={sending}
                    readOnly={listening && !editing}
                    onFocus={() => {
                      setEditing(true);
                      setReply(null);
                      setError("");
                    }}
                    onChange={(e) => {
                      userEditedRef.current = true;
                      setEditing(true);
                      setDraft(e.target.value);
                      setLivePartial("");
                      setTranscriptReady(Boolean(e.target.value.trim()));
                    }}
                    placeholder={
                      contextPage === "live_training"
                        ? t("liveTrainingPlanVoicePlaceholder") ||
                          "Add exercise, use template, new station, AI plan…"
                        : t("planningVoicePlaceholder") ||
                          "Add exercise, apply template, create personalized…"
                    }
                  />
                  {error ? (
                    <p className="lt-pingflo-voice__error" role="alert">
                      {error}
                    </p>
                  ) : null}
                  {reply ? (
                    <p className="lt-pingflo-voice__reply" role="status">
                      {reply}
                    </p>
                  ) : null}
                </div>
                <LevelMeter level={level} active={listening} />
                <CoachVoiceButton
                  className="lt-pingflo-voice__mic"
                  languageHint={languageHint}
                  disabled={micDisabled}
                  autoStartToken={autoStartToken}
                  onPartial={handlePartial}
                  onListeningChange={handleListeningChange}
                  onLevelChange={setLevel}
                  onTranscript={handleTranscript}
                />
                <button
                  type="button"
                  className="lt-pingflo-voice__send"
                  disabled={!canSend}
                  onClick={() => {
                    if (!transcriptReady || listening) return;
                    const visible = String(textareaRef.current?.value || "").trim();
                    const text = visible || String(draft || "").trim();
                    void sendCommand(text, voiceLanguage);
                  }}
                  aria-label={t("send") || "Send"}
                >
                  <SendIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="lt-pingflo-voice__close"
                  onClick={() => {
                    setOpen(false);
                    clearCaptionText();
                    freshCommandRef.current = true;
                  }}
                  aria-label={t("close") || "Close"}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            </div>,
            captionHost
          )
        : null}

      <button
        type="button"
        className={`planning-pingflo__mic${listening ? " is-listening" : ""}${
          open ? " is-open" : ""
        }`}
        disabled={micDisabled}
        onClick={openMic}
        aria-label={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
        title={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
      >
        <span className="planning-pingflo__mark" aria-hidden>
          <DigipingMark
            agentId="pingflo"
            size={20}
            primary={primaryColor}
            showGlow={listening}
            title={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
          />
        </span>
      </button>
    </div>
  );
}

export default PlanningPingfloVoice;
